import { PlanFeatureGate } from '@/components/subscription/PlanFeatureGate';
import { lazy } from 'react';
import {
  Routes,
  Route,
  Navigate,
  generatePath,
  useLocation,
  useParams,
} from 'react-router-dom';

// Dev-only design-system gallery. Excluded from production builds.
const DsPreview = import.meta.env.DEV
  ? lazy(() => import('@/pages/__preview/DsPreview'))
  : null;

// Dev-only harness for the auth screens that sit behind a session guard.
const AuthPreview = import.meta.env.DEV
  ? lazy(() => import('@/pages/__preview/AuthPreview'))
  : null;

// Dev-only harness for the availability editor, which sits behind auth, an
// clinic and a branch.
const AvailabilityPreview = import.meta.env.DEV
  ? lazy(() => import('@/pages/__preview/AvailabilityPreview'))
  : null;

// Dev-only harness for the printable patient documents (statement, receipt).
const DocumentsPreview = import.meta.env.DEV
  ? lazy(() => import('@/pages/__preview/DocumentsPreview'))
  : null;

// Dev-only harness for the route error boundary's fallback, which is
// otherwise only reachable by breaking a real page.
const CrashPreview = import.meta.env.DEV
  ? lazy(() => import('@/pages/__preview/CrashPreview'))
  : null;
import { ProtectedRoute, PublicRoute } from '@/components';
import { Lazy } from '@/components/LazyLoad';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';
import { ShellHost } from '@/components/layout/shell/ShellHost';
import { SubscriptionGuard } from '@/components/subscription/SubscriptionGuard';
import { useAuth } from '@/contexts/AuthContext';
import { hasPermission, ActionsEnum } from '@/lib/permissions';
import {
  Login,
  ForgotPassword,
  ResetPassword,
  InvitationSignup,
  ConsentSign,
  PlanReview,
  MedicalFormFill,
  PlanPresentation,
  PendingInvitations,
  VerifyEmail,
  SelectClinic,
  ClinicSetup,
  DeactivatedPage,
  Patients,
  PatientImports,
  PatientImportJob,
  IncomeImports,
  IncomeImportJob,
  ExpenseImports,
  ExpenseImportJob,
  AppointmentsCalendar,
  AppointmentsList,
  PendingReservations,
  Waitlist,
  Recalls,
  DentalChartDemo,
  PatientProfile,
  PatientQrRedirect,
  PatientLedger,
  ReservationDetails,
  LabRequestDetails,
  LabRequestsPage,
  LabsPage,
  LabPartnerDetailsPage,
  Finance,
  FinanceIncome,
  FinanceExpenses,
  PlanInstalments,
  FinanceExpenseDetails,
  FinanceInvoiceDetails,
  FinanceTransactionDetails,
  FinanceTransactions,
  FinanceMonthlyReport,
  FinanceAccounts,
  FinanceAssets,
  FinanceOwnerEquity,
  FinanceProfitLoss,
  FinanceBalanceSheet,
  FinanceCashFlow,
  Chat,
  ProcedureDetails,
  MyHome,
  ReceptionistQueue,
  DoctorQueueEnhanced,
  WaitingRoomDisplay,
  AdvancedAnalytics,
  ReferralSources,
  PatientRiskReport,
  ActivityLogPage,
  FinanceReports,
  PayrollPage,
  PayrollPayoutDetails,
  MyEarnings,
  NotificationsPage,
  InsuranceOverviewPage,
  InsuranceClaimsPage,
  ClaimDetailPage,
  InsuranceBatchesPage,
  InsuranceCompaniesPage,
  InsuranceCompanyDetailPage,
  InsurancePlanDetailPage,
} from '@/pages/lazy';
import {
  CashCollectionReport,
  ExpenseSummaryReport,
  RefundsReport,
  ProductionReport,
  OffersLoyaltyReport,
  TreatmentProfitabilityReport,
  PatientIncomeReport,
  BranchComparisonReport,
  ARAgingReport,
  PatientStatementReport,
} from '@/pages/reports/lazy';
import {
  StockLevels,
  MaterialStockDetail,
  StockPurchases,
  NewStockPurchase,
  PurchaseDetails,
  UsagePage,
  StockCount,
  StockTransfers,
  SuppliersPage,
  SupplierDetailsPage,
} from '@/pages/inventory/lazy';
import {
  SettingsLayout,
  SettingsIndex,
  SettingsSectionRoute,
  StaffDetailsPage,
  RoleEditorPage,
  ProcedureEditorPage,
} from '@/pages/settings/lazy';
import { FeedbackPage } from '@/pages/FeedbackPage';
import { TicketDetailPage } from '@/pages/TicketDetailPage';
import { SentryTestPage } from '@/pages/SentryTestPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import {
  PrintableReceipt,
  PrintableVisitStatement,
  PrintableInvoice,
  PrintableExpense,
  PrintablePrescription,
  PrintableTreatmentPlanStatement,
  PrintablePayslip,
  PrintableConsent,
  PrintablePlanAcceptance,
  PrintablePatientCard,
} from '@/pages/print/lazy';
import {
  financeRoutePaths,
  reportRoutePaths,
  inventoryRoutePaths,
  queueRoutePaths,
  printRoutePaths,
  dashboardRoutePaths,
  labRoutePaths,
  payrollRoutePaths,
  consentSignPath,
  planReviewPath,
  medicalFormPath,
  patientQrRoutePath,
  setupRoutePath,
  planPresentationPath,
} from './route-config';

/**
 * Helper component to wrap protected routes with clinic requirement
 *
 * The error boundary sits INSIDE the guards and OUTSIDE `Lazy`, so it catches
 * both a page that throws while rendering and a chunk that fails to load —
 * while the auth and subscription guards above it stay outside its blast
 * radius. Its fallback re-renders the app shell, so a broken page leaves the
 * sidebar and every other route reachable. See RouteErrorBoundary.
 */
function ProtectedClinicRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute requireClinic={true}>
      <SubscriptionGuard>
        <RouteErrorBoundary>
          <Lazy>{children}</Lazy>
        </RouteErrorBoundary>
      </SubscriptionGuard>
    </ProtectedRoute>
  );
}

/**
 * Helper component for auth-only routes (no clinic required)
 *
 * `chrome="page"`: these render before an clinic is selected, so there
 * is no shell for the error card to fall back into.
 */
function ProtectedAuthRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute>
      <RouteErrorBoundary chrome="page">
        <Lazy>{children}</Lazy>
      </RouteErrorBoundary>
    </ProtectedRoute>
  );
}

/**
 * Public route wrapper
 */
function PublicLazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <PublicRoute>
      <RouteErrorBoundary chrome="page">
        <Lazy>{children}</Lazy>
      </RouteErrorBoundary>
    </PublicRoute>
  );
}

/**
 * Lazy route without auth
 */
function LazyRoute({ children }: { children: React.ReactNode }) {
  return (
    <RouteErrorBoundary chrome="page">
      <Lazy>{children}</Lazy>
    </RouteErrorBoundary>
  );
}

/**
 * Redirects /dashboard/queue to the most relevant queue for the user's role:
 * - RECEPTIONIST → reception queue
 * - DOCTOR / ASSISTANT / HYGIENIST → doctor queue
 * - ADMIN / OTHER / no role → reception queue (oversight default)
 */
/**
 * The stock page used to live at /dashboard/materials under the title
 * "Materials Inventory" while the nav and breadcrumb called it Stock Levels.
 * Old links (the home page's alert rows, bookmarks) keep working — with their
 * query string, which carries the status filter.
 */
/** A moved address: same params, same query string, new path. */
function LegacyRedirect({ to }: { to: string }) {
  const params = useParams();
  const { search } = useLocation();
  return <Navigate to={`${generatePath(to, params)}${search}`} replace />;
}

function StockLevelsRedirect() {
  const { search } = useLocation();
  return <Navigate to={inventoryRoutePaths.stockLevels + search} replace />;
}

function QueueRootRedirect() {
  const { user } = useAuth();
  const doctorSideRoles: Array<string | undefined> = [
    'DOCTOR',
    'ASSISTANT',
    'HYGIENIST',
  ];
  const target = doctorSideRoles.includes(user?.clinicRole)
    ? queueRoutePaths.doctor
    : queueRoutePaths.receptionist;
  return <Navigate to={target} replace />;
}

/**
 * Redirects to home if user lacks the required resource permission
 */
function PermissionGuard({
  children,
  resource,
}: {
  children: React.ReactNode;
  resource: string;
}) {
  const { permissions } = useAuth();
  if (!hasPermission(permissions, resource, ActionsEnum.VIEW)) {
    return <Navigate to={dashboardRoutePaths.home} replace />;
  }
  return <>{children}</>;
}

/**
 * Protected clinic route with additional resource permission check
 */
function PermissionClinicRoute({
  children,
  resource,
}: {
  children: React.ReactNode;
  resource: string;
}) {
  return (
    <ProtectedRoute requireClinic={true}>
      <SubscriptionGuard>
        <PermissionGuard resource={resource}>
          <RouteErrorBoundary>
            <Lazy>{children}</Lazy>
          </RouteErrorBoundary>
        </PermissionGuard>
      </SubscriptionGuard>
    </ProtectedRoute>
  );
}

/**
 * Every /dashboard page renders inside the one shell ShellHost mounts, so the
 * shell survives navigation instead of being rebuilt by each page.
 */
export function AppRoutes() {
  return (
    <ShellHost>
      <RouteTable />
    </ShellHost>
  );
}

function RouteTable() {
  return (
    <Routes>
      {DsPreview && <Route path="/__ds" element={<DsPreview />} />}
      {AuthPreview && <Route path="/__auth" element={<AuthPreview />} />}
      {DocumentsPreview && (
        <Route path="/__documents" element={<DocumentsPreview />} />
      )}
      {AvailabilityPreview && (
        <Route path="/__availability" element={<AvailabilityPreview />} />
      )}
      {CrashPreview && <Route path="/__crash" element={<CrashPreview />} />}
      {/* ==================== PUBLIC ROUTES ==================== */}
      <Route
        path="/"
        element={
          <PublicLazyRoute>
            <Login />
          </PublicLazyRoute>
        }
      />
      <Route
        path="/login"
        element={
          <PublicLazyRoute>
            <Login />
          </PublicLazyRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicLazyRoute>
            <ForgotPassword />
          </PublicLazyRoute>
        }
      />
      <Route
        path="/reset-password"
        element={
          <LazyRoute>
            <ResetPassword />
          </LazyRoute>
        }
      />
      {/* A patient's signing link. No account, so no auth wrapper: a member
          of staff who is signed in on the same device still sees the form. */}
      <Route
        path={consentSignPath}
        element={
          <LazyRoute>
            <ConsentSign />
          </LazyRoute>
        }
      />
      {/* A patient's plan review link: public, like the signing link. */}
      <Route
        path={planReviewPath}
        element={
          <LazyRoute>
            <PlanReview />
          </LazyRoute>
        }
      />
      {/* A patient's medical history link: public, like the signing link. */}
      <Route
        path={medicalFormPath}
        element={
          <LazyRoute>
            <MedicalFormFill />
          </LazyRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicLazyRoute>
            <InvitationSignup />
          </PublicLazyRoute>
        }
      />
      <Route
        path="/dental-chart-demo"
        element={
          <LazyRoute>
            <DentalChartDemo />
          </LazyRoute>
        }
      />

      {/* ==================== AUTH ROUTES (No Clinic Required) ==================== */}
      <Route
        path="/verify-email"
        element={
          <ProtectedAuthRoute>
            <VerifyEmail />
          </ProtectedAuthRoute>
        }
      />
      <Route
        path="/select-clinic"
        element={
          <ProtectedAuthRoute>
            <SelectClinic />
          </ProtectedAuthRoute>
        }
      />
      <Route
        path="/invitations"
        element={
          <ProtectedAuthRoute>
            <PendingInvitations />
          </ProtectedAuthRoute>
        }
      />
      {/* Full screen, no shell: setup comes before the dashboard exists for
          this clinic. The error card therefore renders as a page. */}
      <Route
        path={setupRoutePath}
        element={
          <ProtectedRoute requireClinic={true}>
            <SubscriptionGuard>
              <RouteErrorBoundary chrome="page">
                <Lazy>
                  <ClinicSetup />
                </Lazy>
              </RouteErrorBoundary>
            </SubscriptionGuard>
          </ProtectedRoute>
        }
      />
      <Route
        path="/deactivated"
        element={
          <ProtectedAuthRoute>
            <DeactivatedPage />
          </ProtectedAuthRoute>
        }
      />

      {/* ==================== DASHBOARD REDIRECTS ==================== */}
      <Route
        path={dashboardRoutePaths.root}
        element={<Navigate to={dashboardRoutePaths.home} replace />}
      />
      <Route path={queueRoutePaths.root} element={<QueueRootRedirect />} />
      <Route
        path={labRoutePaths.root}
        element={<Navigate to={labRoutePaths.requests} replace />}
      />

      {/* ==================== MAIN DASHBOARD ROUTES ==================== */}
      <Route
        path={dashboardRoutePaths.home}
        element={
          <ProtectedClinicRoute>
            <MyHome />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.patients}
        element={
          <ProtectedClinicRoute>
            <Patients />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.patientImports}
        element={
          <ProtectedClinicRoute>
            <PatientImports />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.patientImportJob}
        element={
          <ProtectedClinicRoute>
            <PatientImportJob />
          </ProtectedClinicRoute>
        }
      />
      {/* Where a patient's QR code lands. Signed-in staff only. */}
      <Route
        path={patientQrRoutePath}
        element={
          <ProtectedClinicRoute>
            <PatientQrRedirect />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.patientProfile}
        element={
          <ProtectedClinicRoute>
            <PatientProfile />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.patientLedger}
        element={
          <PermissionClinicRoute resource="finance">
            <PatientLedger />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.reservations}
        element={
          <ProtectedClinicRoute>
            <AppointmentsCalendar />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.reservationsList}
        element={
          <ProtectedClinicRoute>
            <AppointmentsList />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.pendingReservations}
        element={
          <ProtectedClinicRoute>
            <PendingReservations />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.waitlist}
        element={
          <ProtectedClinicRoute>
            <Waitlist />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.recalls}
        element={
          <ProtectedClinicRoute>
            <Recalls />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.reservationDetails}
        element={
          <ProtectedClinicRoute>
            <ReservationDetails />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.reservationReceipt}
        // The old in-app receipt kept its own copy of the money rules and had
        // drifted from the printed one. One receipt now, at its print address.
        element={<LegacyRedirect to={printRoutePaths.receipt} />}
      />
      <Route
        path={dashboardRoutePaths.procedureDetails}
        element={
          <ProtectedClinicRoute>
            <ProcedureDetails />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.materials}
        element={<StockLevelsRedirect />}
      />
      {/* The first-generation list lived here. It is gone; the work board at
          /dashboard/labs/requests replaces it. Kept as a redirect because the
          home page's "lab overdue" row and any bookmarked link point here. */}
      <Route
        path={dashboardRoutePaths.labRequests}
        element={<Navigate to={labRoutePaths.requests} replace />}
      />
      <Route
        path={dashboardRoutePaths.labRequestDetails}
        element={
          <PermissionClinicRoute resource="lab_requests">
            <LabRequestDetails />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.chat}
        element={
          <ProtectedClinicRoute>
            <PlanFeatureGate feature="staff_chat">
              <Chat />
            </PlanFeatureGate>
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.feedback}
        element={
          <ProtectedClinicRoute>
            <FeedbackPage />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.feedbackDetail}
        element={
          <ProtectedClinicRoute>
            <TicketDetailPage />
          </ProtectedClinicRoute>
        }
      />
      {/* Settings is a nested tree, not a page. The layout renders once and
          the sections swap inside its <Outlet />, so the shell does not
          remount between sections and each section code-splits on its own.
          The index also absorbs legacy `?tab=` links. */}
      <Route
        path={dashboardRoutePaths.settings}
        element={
          <ProtectedClinicRoute>
            <SettingsLayout />
          </ProtectedClinicRoute>
        }
      >
        <Route index element={<SettingsIndex />} />
        <Route path="staff/:staffId" element={<StaffDetailsPage />} />
        {/* The role editor is a page, not a modal — 60 permissions with search
            and a live diff do not fit a dialog. Declared before :section so
            `roles/new` is not swallowed as a section named "roles". */}
        <Route path="roles/:roleId" element={<RoleEditorPage />} />
        {/* Same reasoning as the role editor: details, default materials and
            lab requirements need to sit on one surface with the live margin
            preview, which no dialog has room for. Also before :section, so
            `procedures/new` is not read as a section named "procedures". */}
        <Route
          path="procedures/:procedureId"
          element={<ProcedureEditorPage />}
        />
        <Route path=":section" element={<SettingsSectionRoute />} />
      </Route>
      <Route
        path={dashboardRoutePaths.advancedAnalytics}
        element={
          <PermissionClinicRoute resource="analytics">
            <PlanFeatureGate feature="clinic_analytics">
              <AdvancedAnalytics />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />

      <Route
        path={dashboardRoutePaths.referralSources}
        element={
          <PermissionClinicRoute resource="analytics">
            <PlanFeatureGate feature="clinic_analytics">
              <ReferralSources />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />

      {/* Same gate as the API: the audit list answers settings:view. */}
      <Route
        path={dashboardRoutePaths.activityLog}
        element={
          <PermissionClinicRoute resource="settings">
            <PlanFeatureGate feature="activity_log">
              <ActivityLogPage />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />

      <Route
        path={dashboardRoutePaths.patientRiskReport}
        element={
          <PermissionClinicRoute resource="analytics">
            <PlanFeatureGate feature="clinic_analytics">
              <PatientRiskReport />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />

      <Route
        path={dashboardRoutePaths.notifications}
        element={
          <ProtectedClinicRoute>
            <NotificationsPage />
          </ProtectedClinicRoute>
        }
      />

      {/* ==================== INSURANCE ROUTES ==================== */}
      <Route
        path={dashboardRoutePaths.insurance}
        element={
          <ProtectedClinicRoute>
            <PlanFeatureGate feature="insurance">
              <InsuranceOverviewPage />
            </PlanFeatureGate>
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.insuranceClaims}
        element={
          <ProtectedClinicRoute>
            <PlanFeatureGate feature="insurance">
              <InsuranceClaimsPage />
            </PlanFeatureGate>
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.insuranceClaimDetail}
        element={
          <ProtectedClinicRoute>
            <ClaimDetailPage />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.insuranceBatches}
        element={
          // Batches are insurer receivables: finance, like the API route.
          <PermissionClinicRoute resource="finance">
            <PlanFeatureGate feature="insurance">
              <InsuranceBatchesPage />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      {/*
        Companies, and the two detail routes the list used to hold as component
        state. Gated on `finance` like the Finance section: these screens carry
        contracted prices, receivable balances and collection rates, which is
        the same class of information and was previously reachable by anyone
        who could open the dashboard.
      */}
      <Route
        path={dashboardRoutePaths.insuranceCompanies}
        element={
          <PermissionClinicRoute resource="finance">
            <PlanFeatureGate feature="insurance">
              <InsuranceCompaniesPage />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.insuranceCompanyDetail}
        element={
          <PermissionClinicRoute resource="finance">
            <InsuranceCompanyDetailPage />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={dashboardRoutePaths.insurancePlanDetail}
        element={
          <PermissionClinicRoute resource="finance">
            <InsurancePlanDetailPage />
          </PermissionClinicRoute>
        }
      />

      {/* ==================== FINANCE ROUTES ==================== */}
      <Route
        path={financeRoutePaths.root}
        element={
          <PermissionClinicRoute resource="finance">
            <Finance />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.income}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceIncome />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.incomeImports}
        element={
          <PermissionClinicRoute resource="finance">
            <IncomeImports />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.incomeImportJob}
        element={
          <PermissionClinicRoute resource="finance">
            <IncomeImportJob />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.instalments}
        element={
          <PermissionClinicRoute resource="finance">
            <PlanInstalments />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.expenses}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceExpenses />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.expenseImports}
        element={
          <PermissionClinicRoute resource="finance">
            <ExpenseImports />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.expenseImportJob}
        element={
          <PermissionClinicRoute resource="finance">
            <ExpenseImportJob />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.expenseDetails}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceExpenseDetails />
          </PermissionClinicRoute>
        }
      />
      {/*
        Finance → Payments is gone. It was the ledger filtered to patient
        payments, summed in the browser, with a payment-method donut. The same
        rows are at Transactions with `direction=in&source=PAYMENT`, where the
        totals come from the server rather than from whatever page happened to
        be loaded, and the method breakdown is on the Cash control report.

        This page was never in the sidebar; it was reached from notification
        rows and from two drill-throughs, all of which now point at the
        filtered ledger.
      */}
      <Route
        path={financeRoutePaths.payments}
        element={
          <Navigate
            to={`${financeRoutePaths.transactions}?direction=in&source=PAYMENT`}
            replace
          />
        }
      />
      <Route
        path={financeRoutePaths.invoiceDetails}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceInvoiceDetails />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.transactions}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceTransactions />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.transactionDetails}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceTransactionDetails />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.monthlyReport}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceMonthlyReport />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.accounts}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceAccounts />
          </PermissionClinicRoute>
        }
      />
      {/*
        The Treatment Cost / COGS report is gone. It duplicated the P&L
        (revenue, gross profit, net profit, margin ratios) while its name
        promised per-procedure analysis it never delivered — that lives at
        `reportRoutePaths.treatmentProfitability`. Everything it uniquely
        showed (top consumed materials, material cost ratio, the fixed-expense
        breakdown, inventory vs consumption) is on the monthly report, from
        the same endpoint, so the path lands there rather than nowhere: it was
        in the sidebar and is in people's bookmarks.
      */}
      <Route
        path={financeRoutePaths.cogs}
        element={<Navigate to={financeRoutePaths.monthlyReport} replace />}
      />
      <Route
        path={financeRoutePaths.assets}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceAssets />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.ownerEquity}
        element={
          <PermissionClinicRoute resource="finance">
            <FinanceOwnerEquity />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.balanceSheet}
        element={
          <PermissionClinicRoute resource="finance">
            <PlanFeatureGate feature="financial_statements">
              <FinanceBalanceSheet />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={financeRoutePaths.cashFlow}
        element={
          <PermissionClinicRoute resource="finance">
            <PlanFeatureGate feature="financial_statements">
              <FinanceCashFlow />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      {/* Practice Health moved to the Reports root: two of its metrics are
          operational rather than financial, and Reports was a link directory
          that computed nothing. Bookmarks and the old nav entry still resolve. */}
      <Route
        path={financeRoutePaths.practiceHealth}
        element={<Navigate to={reportRoutePaths.root} replace />}
      />
      <Route
        path={financeRoutePaths.profitLoss}
        element={
          <PermissionClinicRoute resource="finance">
            <PlanFeatureGate feature="financial_statements">
              <FinanceProfitLoss />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />

      {/* ==================== REPORTS ROUTES ==================== */}
      <Route
        path={reportRoutePaths.root}
        element={
          <PermissionClinicRoute resource="reports">
            <FinanceReports />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={reportRoutePaths.cashCollection}
        element={
          <PermissionClinicRoute resource="reports">
            <CashCollectionReport />
          </PermissionClinicRoute>
        }
      />
      {/*
        Pending Payments is gone. It is Finance → Income filtered to unpaid —
        the same invoices, with fewer columns, no aging, no collection rate and
        no way to take the money. The status tab moved into the URL in Stage E
        precisely so this could become a link.

        One figure is deliberately not carried across: the average per pending
        invoice. It is the total divided by the count, both of which Income
        shows.
      */}
      <Route
        path={reportRoutePaths.pendingPayments}
        element={
          <Navigate to={`${financeRoutePaths.income}?status=open`} replace />
        }
      />
      {/*
        This route was labelled "profit-loss" but rendered the COGS page — the
        same component as `financeRoutePaths.cogs`, under a second name and a
        second permission. It now points at the actual P&L statement.
      */}
      <Route
        path={reportRoutePaths.profitLoss}
        element={<Navigate to={financeRoutePaths.profitLoss} replace />}
      />
      {/*
        The Transaction Ledger report is gone. It was reachable from nowhere —
        no sidebar entry, no card, no link in any page — and Finance →
        Transactions is a strict superset of it: nine filters against its two,
        serialized into the URL so a filtered ledger can be shared, plus a CSV
        export the report never had. Redirected rather than deleted because a
        bookmark may exist even where a link never did.
      */}
      <Route
        path={reportRoutePaths.ledger}
        element={<Navigate to={financeRoutePaths.transactions} replace />}
      />
      {/*
        The KPI dashboard is gone. Of its six tiles, the collection rate was the
        literal `periodRevenue > 0 ? 100 : 0`, outstanding receivables was the
        constant 0, and the two per-appointment figures divided by a count of
        inbound TRANSACTIONS. The three real ones — today's revenue, the
        period's revenue, net profit — are on Finance Overview, computed once
        and compared against the previous period.

        Redirected to the Reports root rather than to Overview: someone who
        bookmarked "KPIs" wants the benchmarked verdict, which is what the
        Practice Health panel at the top of Reports is.
      */}
      <Route
        path={reportRoutePaths.kpi}
        element={<Navigate to={reportRoutePaths.root} replace />}
      />
      <Route
        path={reportRoutePaths.expenseSummary}
        element={
          <PermissionClinicRoute resource="reports">
            <ExpenseSummaryReport />
          </PermissionClinicRoute>
        }
      />
      {/*
        The Accounts Balance report is gone. Finance → Accounts answers the same
        question and answers it better: it carries the cash/bank/online
        subtotals this report had (added in Stage E), plus inflow, outflow,
        per-account reconciliation and the actions to transfer or close.

        It was also the more correct page of the two. This report summed every
        account regardless of currency and rendered the result with a hardcoded
        "$", so a clinic holding a USD account saw a "grand total" that added
        two currencies together. Accounts has always totalled the base currency
        only and said on the page which accounts that leaves out.
      */}
      <Route
        path={reportRoutePaths.accountsBalance}
        element={<Navigate to={financeRoutePaths.accounts} replace />}
      />
      <Route
        path={reportRoutePaths.production}
        element={
          <PermissionClinicRoute resource="staff_compensation">
            <PlanFeatureGate feature="payroll">
              <ProductionReport />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={reportRoutePaths.offersLoyalty}
        element={
          <PermissionClinicRoute resource="reports">
            <PlanFeatureGate feature="loyalty_offers">
              <OffersLoyaltyReport />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={reportRoutePaths.refunds}
        element={
          <PermissionClinicRoute resource="reports">
            <RefundsReport />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={reportRoutePaths.treatmentProfitability}
        element={
          <PermissionClinicRoute resource="reports">
            <PlanFeatureGate feature="financial_statements">
              <TreatmentProfitabilityReport />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={reportRoutePaths.patientIncome}
        element={
          <PermissionClinicRoute resource="reports">
            <PatientIncomeReport />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={reportRoutePaths.branchComparison}
        element={
          <PermissionClinicRoute resource="reports">
            <PlanFeatureGate feature="branch_reports">
              <BranchComparisonReport />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={reportRoutePaths.arAging}
        element={
          <PermissionClinicRoute resource="reports">
            <ARAgingReport />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={reportRoutePaths.patientStatement}
        element={
          <PermissionClinicRoute resource="reports">
            <PatientStatementReport />
          </PermissionClinicRoute>
        }
      />

      {/* ==================== INVENTORY ROUTES ==================== */}
      <Route
        path={inventoryRoutePaths.stockLevels}
        element={
          <PermissionClinicRoute resource="inventory">
            <PlanFeatureGate feature="inventory">
              <StockLevels />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.transfers}
        element={
          <PermissionClinicRoute resource="inventory">
            <PlanFeatureGate feature="inventory">
              <StockTransfers />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.materialStock}
        element={
          <PermissionClinicRoute resource="inventory">
            <MaterialStockDetail />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.purchases}
        element={
          <PermissionClinicRoute resource="inventory">
            <PlanFeatureGate feature="inventory">
              <StockPurchases />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.newPurchase}
        element={
          <PermissionClinicRoute resource="inventory">
            <PlanFeatureGate feature="inventory">
              <NewStockPurchase />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.newPurchaseAlt}
        element={
          <PermissionClinicRoute resource="inventory">
            <PlanFeatureGate feature="inventory">
              <NewStockPurchase />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.purchaseDetails}
        element={
          <PermissionClinicRoute resource="inventory">
            <PurchaseDetails />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.purchaseDetail}
        element={
          <PermissionClinicRoute resource="inventory">
            <PurchaseDetails />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.purchasesAlias}
        element={<Navigate to={inventoryRoutePaths.purchases} replace />}
      />
      {/* Consumption and Material Usage are one page now; the old address
          lands on its log view. */}
      <Route
        path={inventoryRoutePaths.consumption}
        element={
          <Navigate to={`${inventoryRoutePaths.usage}?view=log`} replace />
        }
      />
      <Route
        path={inventoryRoutePaths.usage}
        element={
          <PermissionClinicRoute resource="inventory">
            <PlanFeatureGate feature="inventory">
              <UsagePage />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.stockCount}
        element={
          <PermissionClinicRoute resource="inventory">
            <PlanFeatureGate feature="inventory">
              <StockCount />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.suppliers}
        element={
          <PermissionClinicRoute resource="inventory">
            <PlanFeatureGate feature="inventory">
              <SuppliersPage />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={inventoryRoutePaths.supplierDetail}
        element={
          <PermissionClinicRoute resource="inventory">
            <SupplierDetailsPage />
          </PermissionClinicRoute>
        }
      />
      {/* The supplier pages used to live outside /dashboard. */}
      <Route
        path={inventoryRoutePaths.suppliersLegacy}
        element={<LegacyRedirect to={inventoryRoutePaths.suppliers} />}
      />
      <Route
        path={inventoryRoutePaths.supplierDetailLegacy}
        element={<LegacyRedirect to={inventoryRoutePaths.supplierDetail} />}
      />

      {/* ==================== QUEUE ROUTES ==================== */}
      <Route
        path={queueRoutePaths.receptionist}
        element={
          <ProtectedClinicRoute>
            <ReceptionistQueue />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={queueRoutePaths.doctor}
        element={
          <ProtectedClinicRoute>
            <DoctorQueueEnhanced />
          </ProtectedClinicRoute>
        }
      />
      {/* Wall screen. No DashboardLayout — the room sees the queue, not the
          app. Still behind auth: the branch queue is patient data. */}
      <Route
        path={queueRoutePaths.display}
        element={
          <ProtectedClinicRoute>
            <WaitingRoomDisplay />
          </ProtectedClinicRoute>
        }
      />

      {/* ==================== LAB ROUTES ==================== */}
      <Route
        path={labRoutePaths.requests}
        element={
          <PermissionClinicRoute resource="lab_requests">
            <PlanFeatureGate feature="lab_cases">
              <LabRequestsPage />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />

      <Route
        path={labRoutePaths.labsList}
        element={
          <PermissionClinicRoute resource="lab_requests">
            <PlanFeatureGate feature="lab_cases">
              <LabsPage />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={labRoutePaths.labDetails}
        element={
          <PermissionClinicRoute resource="lab_requests">
            <LabPartnerDetailsPage />
          </PermissionClinicRoute>
        }
      />

      {/* ==================== PRINT ROUTES ==================== */}
      <Route
        path={printRoutePaths.receipt}
        element={
          <ProtectedClinicRoute>
            <PrintableReceipt />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.visitStatement}
        element={
          <ProtectedClinicRoute>
            <PrintableVisitStatement />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.invoice}
        element={
          <PermissionClinicRoute resource="finance">
            <PrintableInvoice />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.expense}
        element={
          <PermissionClinicRoute resource="finance">
            <PrintableExpense />
          </PermissionClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.prescription}
        element={
          <ProtectedClinicRoute>
            <PrintablePrescription />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.consent}
        element={
          <ProtectedClinicRoute>
            <PrintableConsent />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.treatmentPlanStatement}
        element={
          <ProtectedClinicRoute>
            <PrintableTreatmentPlanStatement />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.patientCard}
        element={
          <ProtectedClinicRoute>
            <PrintablePatientCard />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.planAcceptance}
        element={
          <ProtectedClinicRoute>
            <PrintablePlanAcceptance />
          </ProtectedClinicRoute>
        }
      />
      {/* The plan shown to the patient on the clinic's tablet. No
          DashboardLayout: the patient sees the plan, not the app. Still
          behind auth, since it is the patient's record. */}
      <Route
        path={planPresentationPath}
        element={
          <ProtectedClinicRoute>
            <PlanPresentation />
          </ProtectedClinicRoute>
        }
      />
      <Route
        path={printRoutePaths.payslip}
        element={
          <PermissionClinicRoute resource="payroll">
            <PrintablePayslip />
          </PermissionClinicRoute>
        }
      />

      {/* ==================== PAYROLL ROUTES ==================== */}
      <Route
        path={payrollRoutePaths.root}
        element={
          <PermissionClinicRoute resource="payroll">
            <PlanFeatureGate feature="payroll">
              <PayrollPage />
            </PlanFeatureGate>
          </PermissionClinicRoute>
        }
      />
      <Route
        path={payrollRoutePaths.payoutDetails}
        element={
          <PermissionClinicRoute resource="payroll">
            <PayrollPayoutDetails />
          </PermissionClinicRoute>
        }
      />
      {/* Own earnings: session-scoped, no payroll permission. The API resolves
          the staff id from the token, so this cannot show anyone else's pay. */}
      <Route
        path={payrollRoutePaths.myEarnings}
        element={
          <ProtectedClinicRoute>
            <PlanFeatureGate feature="payroll">
              <MyEarnings />
            </PlanFeatureGate>
          </ProtectedClinicRoute>
        }
      />

      {/* ==================== INTERNAL TOOLS ==================== */}
      <Route path="/sentry-test" element={<SentryTestPage />} />

      {/* ==================== CATCH ALL ==================== */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
